import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useApi } from "@/hooks/useApi";
import { Layout } from "@/components/Layout";
import { AnnouncementBanner } from "@/components/AnnouncementBanner";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TrendingUp, Users, FileText, Activity, Calendar, UserCog, Shield, Trash2, Edit, Plus, Send, Search, ArrowUp, ArrowDown, Mail, MessageCircle, Loader2, Bell, X } from "lucide-react";
import { toast } from "sonner";

export const AdminDashboardPage = () => {
  const { user, token } = useAuth();
  const api = useApi(token);
  const navigate = useNavigate();
  
  const [clinics, setClinics] = useState([]);
  const [selectedClinic, setSelectedClinic] = useState("");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  
  // Clinic Settings (SMS Footer)
  const [smsFooter, setSmsFooter] = useState("");
  const [smsFooterLoading, setSmsFooterLoading] = useState(false);
  
  // Clinic Edit Dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    address: "",
    email: "",
    phones: "",
    website: "",
    services: "",
    gps_coordinates: "",
    opening_hours: ""
  });
  
  // Audit Logs
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditDateFilter, setAuditDateFilter] = useState({
    startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  
  // User Management
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Individual SMS Preview state
  const [smsPreviewOpen, setSmsPreviewOpen] = useState(false);
  const [smsToSend, setSmsToSend] = useState(null);
  
  const [userForm, setUserForm] = useState({
    full_name: "",
    email: "",
    password: "",
    role: "nurse",
    clinic_id: ""
  });
  
  // Patients
  const [patients, setPatients] = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(false);
  
  // Checklist Settings
  const [checklistSettings, setChecklistSettings] = useState({
    notification_times: ["12:00", "15:30"],
    notification_recipients: ["admin", "assigned_nurse"],
    sms_enabled: true,
    email_enabled: true
  });
  const [checklistSettingsLoading, setChecklistSettingsLoading] = useState(false);
  const [newNotificationTime, setNewNotificationTime] = useState("");

  const [patientFilters, setPatientFilters] = useState({
    gender: "all",
    minAge: "",
    maxAge: "",
    search: ""
  });
  const [patientSearch, setPatientSearch] = useState("");
  const [patientSortField, setPatientSortField] = useState("full_name");
  const [patientSortOrder, setPatientSortOrder] = useState("asc");
  
  // CSV Import
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [showColumnMapper, setShowColumnMapper] = useState(false);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvData, setCsvData] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  
  // Bulk notification state
  const [bulkNotificationOpen, setBulkNotificationOpen] = useState(false);
  const [bulkDateRange, setBulkDateRange] = useState({
    startDate: "",
    endDate: ""
  });
  const [sendingBulk, setSendingBulk] = useState(false);

  // Follow-ups
  const [followUps, setFollowUps] = useState([]);
  const [followUpsLoading, setFollowUpsLoading] = useState(false);

  const [activeTab, setActiveTab] = useState("overview");
  const isSuperadmin = user?.role === "superadmin";
  const isClinicAdmin = user?.role === "clinic_admin";

  // Load clinics on mount
  useEffect(() => {
    if (user) {
      loadClinics();
    }
  }, [user]);

  // Load data when clinic or date changes
  useEffect(() => {
    if (selectedClinic) {
      loadClinicSmsFooter(); // Load SMS footer for selected clinic
      if (activeTab === "overview") {
        loadStats();
        loadFollowUps();
      }
      if (activeTab === "audit") loadAuditLogs();
      if (activeTab === "users") loadUsers();
      if (activeTab === "patients") loadPatients();
    }
  }, [selectedClinic, dateRange, activeTab]);

  // Reload patients when filters change
  useEffect(() => {
    if (selectedClinic && activeTab === "patients") {
      loadPatients();
    }
  }, [patientFilters]);

  // Load checklist settings when clinic selected
  useEffect(() => {
    if (selectedClinic && activeTab === "settings") {
      loadChecklistSettings();
    }
  }, [selectedClinic, activeTab]);

  const loadClinics = async () => {
    try {
      const data = await api.get("/clinics");
      setClinics(data);
      
      if (data.length > 0) {
        if (isClinicAdmin && user?.clinic_id) {
          const userClinic = data.find(c => c.id === user.clinic_id);
          if (userClinic) {
            setSelectedClinic(userClinic.id);
          } else if (data.length === 1) {
            setSelectedClinic(data[0].id);
          }
        } else if (isSuperadmin) {
          setSelectedClinic(data[0].id);
        }
      }
    } catch (error) {
      console.error("Failed to load clinics:", error);
      toast.error("Failed to load clinics");
    }
  };

  const loadClinicSmsFooter = async () => {
    if (!selectedClinic || selectedClinic === "all") return;
    
    try {
      const clinic = await api.get(`/clinics/${selectedClinic}`);
      setSmsFooter(clinic.sms_footer || "");
    } catch (error) {
      console.error("Failed to load clinic SMS footer:", error);
    }
  };

  const saveClinicSmsFooter = async () => {
    if (!selectedClinic || selectedClinic === "all") return;
    
    setSmsFooterLoading(true);
    try {
      await api.put(`/clinics/${selectedClinic}`, { sms_footer: smsFooter });
      toast.success("SMS Footer saved successfully!");
    } catch (error) {
      console.error("Failed to save SMS footer:", error);
      toast.error("Failed to save SMS footer");
    } finally {
      setSmsFooterLoading(false);
    }
  };

  const loadStats = async () => {
    if (!selectedClinic || selectedClinic === "all") return;
    
    setLoading(true);
    try {
      const data = await api.get(
        `/clinics/${selectedClinic}/stats?start_date=${dateRange.start}&end_date=${dateRange.end}`
      );
      setStats(data);
    } catch (error) {
      console.error("Failed to load stats:", error);
      toast.error("Failed to load statistics");
    } finally {
      setLoading(false);
    }
  };

  const loadAuditLogs = async () => {
    if (!selectedClinic) return;
    
    setAuditLoading(true);
    try {
      const params = new URLSearchParams({
        clinic_id: selectedClinic,
        start_date: auditDateFilter.startDate,
        end_date: auditDateFilter.endDate,
        limit: "100"
      });
      
      const data = await api.get(`/admin/audit-logs?${params.toString()}`);
      // Backend returns {logs: [...]} wrapper, extract the array
      setAuditLogs(data.logs || []);
    } catch (error) {
      console.error("Failed to load audit logs:", error);
      toast.error("Failed to load audit logs");
    } finally {
      setAuditLoading(false);
    }
  };

  const loadUsers = async () => {
    if (!selectedClinic) return;
    
    setUsersLoading(true);
    try {
      // For superadmin with "all" selected, don't filter by clinic
      const url = (isSuperadmin && selectedClinic === "all") 
        ? `/users/` 
        : `/users/?clinic_id=${selectedClinic}`;
      const data = await api.get(url);
      setUsers(data);
    } catch (error) {
      console.error("Failed to load users:", error);
      toast.error("Failed to load users");
    } finally {
      setUsersLoading(false);
    }
  };

  const loadPatients = async () => {
    if (!selectedClinic || selectedClinic === "all") return;
    
    setPatientsLoading(true);
    try {
      const params = new URLSearchParams({
        clinic_id: selectedClinic
      });
      
      // Only add filter if not "all"
      if (patientFilters.gender && patientFilters.gender !== "all") {
        params.append("gender", patientFilters.gender);
      }
      if (patientFilters.minAge) params.append("min_age", patientFilters.minAge);
      if (patientFilters.maxAge) params.append("max_age", patientFilters.maxAge);
      
      const data = await api.get(`/admin/patients-filtered?${params.toString()}`);
      // Backend returns {patients: [...]} wrapper, extract the array
      setPatients(Array.isArray(data) ? data : (data.patients || []));
    } catch (error) {
      console.error("Failed to load patients:", error);
      toast.error("Failed to load patients");
    } finally {
      setPatientsLoading(false);
    }
  };

  const loadChecklistSettings = async () => {
    try {
      const data = await api.get("/checklist/settings");
      if (data) {
        setChecklistSettings(data);
      }
    } catch (error) {
      console.error("Failed to load checklist settings:", error);
    }
  };

  const saveChecklistSettings = async () => {
    setChecklistSettingsLoading(true);
    try {
      await api.put("/checklist/settings", checklistSettings);
      toast.success("Checklist notification settings saved");
    } catch (error) {
      console.error("Failed to save checklist settings:", error);
      toast.error("Failed to save checklist settings");
    } finally {
      setChecklistSettingsLoading(false);
    }
  };

  const addNotificationTime = () => {
    if (newNotificationTime && !checklistSettings.notification_times.includes(newNotificationTime)) {
      setChecklistSettings({
        ...checklistSettings,
        notification_times: [...checklistSettings.notification_times, newNotificationTime]
      });
      setNewNotificationTime("");
    }
  };

  const removeNotificationTime = (time) => {
    setChecklistSettings({
      ...checklistSettings,
      notification_times: checklistSettings.notification_times.filter(t => t !== time)
    });
  };

  const toggleRecipient = (recipient) => {
    const recipients = checklistSettings.notification_recipients;
    if (recipients.includes(recipient)) {
      setChecklistSettings({
        ...checklistSettings,
        notification_recipients: recipients.filter(r => r !== recipient)
      });
    } else {
      setChecklistSettings({
        ...checklistSettings,
        notification_recipients: [...recipients, recipient]
      });
    }
  };

  const handlePatientSort = (field) => {
    if (patientSortField === field) {
      setPatientSortOrder(patientSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setPatientSortField(field);
      setPatientSortOrder('asc');
    }
  };

  // Filter and sort patients locally
  const filteredAndSortedPatients = patients
    .filter(p => {
      if (!patientSearch) return true;
      const search = patientSearch.toLowerCase();
      return (
        p.full_name?.toLowerCase().includes(search) ||
        p.email?.toLowerCase().includes(search) ||
        p.phone?.includes(search) ||
        p.patient_id?.toLowerCase().includes(search) ||
        p.id?.toLowerCase().includes(search)
      );
    })
    .sort((a, b) => {
      let aVal, bVal;
      
      if (patientSortField === 'age') {
        aVal = calculateAge(a.date_of_birth);
        bVal = calculateAge(b.date_of_birth);
      } else if (patientSortField === 'patient_id') {
        aVal = a.patient_id || a.id || '';
        bVal = b.patient_id || b.id || '';
      } else {
        aVal = a[patientSortField] || '';
        bVal = b[patientSortField] || '';
      }
      
      if (aVal < bVal) return patientSortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return patientSortOrder === 'asc' ? 1 : -1;
      return 0;
    });


  const loadFollowUps = async () => {
    if (!selectedClinic || selectedClinic === "all") return;
    
    setFollowUpsLoading(true);
    try {
      // Get follow-ups for next 30 days within date range
      const params = new URLSearchParams({
        clinic_id: selectedClinic,
        days: "30"
      });
      
      const data = await api.get(`/prescriptions/upcoming-followups?${params.toString()}`);
      setFollowUps(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load follow-ups:", error);
      toast.error("Failed to load follow-ups");
    } finally {
      setFollowUpsLoading(false);
    }
  };

  const handleUpdateCallStatus = async (prescriptionId, newStatus, notes = "") => {
    try {
      await api.patch(`/prescriptions/${prescriptionId}/call-status`, {
        call_status: newStatus,
        call_notes: notes
      });
      toast.success("Call status updated");
      await loadFollowUps();
    } catch (error) {
      console.error("Failed to update call status:", error);
      toast.error("Failed to update call status");
    }
  };

  const handleBulkNotification = async () => {
    if (!bulkDateRange.startDate || !bulkDateRange.endDate) {
      toast.error("Please select both start and end dates");
      return;
    }

    setSendingBulk(true);
    try {
      const result = await api.post("/prescriptions/bulk-notification", {
        clinic_id: selectedClinic,
        start_date: bulkDateRange.startDate,
        end_date: bulkDateRange.endDate
      });
      
      toast.success(`Queued ${result.queued_count || 0} follow-up emails`);
      setBulkNotificationOpen(false);
      setBulkDateRange({ startDate: "", endDate: "" });
      await loadFollowUps();
    } catch (error) {
      console.error("Failed to send bulk notifications:", error);
      toast.error(error.response?.data?.detail || "Failed to send bulk notifications");
    } finally {
      setSendingBulk(false);
    }
  };

  const handleSendIndividualEmail = async (prescriptionId) => {
    try {
      const result = await api.post(`/prescriptions/${prescriptionId}/send-followup-email`);
      toast.success("Follow-up email sent successfully");
      await loadFollowUps();
    } catch (error) {
      console.error("Failed to send email:", error);
      toast.error(error.response?.data?.detail || "Failed to send email");
    }
  };

  const handleSendIndividualSms = async (prescriptionId, patientPhone, patientName) => {
    if (!patientPhone) {
      toast.error("Patient phone number not available");
      return;
    }
    
    // Show preview dialog instead of sending immediately
    const defaultMessage = "Reminder: You have an upcoming follow-up appointment. Please confirm.";
    setSmsToSend({
      prescriptionId,
      patientPhone,
      patientName,
      message: defaultMessage
    });
    setSmsPreviewOpen(true);
  };
  
  const handleConfirmSendSms = async () => {
    if (!smsToSend) return;
    
    try {
      const result = await api.post(`/sms/send`, {
        phone: smsToSend.patientPhone,
        content: smsToSend.message,
        encoding: "plain",
        sms_type: "followup",
        prescription_id: smsToSend.prescriptionId
      });
      
      toast.success("Follow-up SMS sent successfully");
      setSmsPreviewOpen(false);
      setSmsToSend(null);
      await loadFollowUps();
    } catch (error) {
      console.error("Failed to send SMS:", error);
      toast.error(error.response?.data?.detail || "Failed to send SMS");
    }
  };


  const handleDeletePatient = async (patientId) => {
    if (!window.confirm("Are you sure you want to delete this patient? This will also delete all their lab results and prescriptions.")) return;
    
    try {
      await api.delete(`/patients/${patientId}`);
      toast.success("Patient deleted successfully");
      await loadPatients();
    } catch (error) {
      console.error("Failed to delete patient:", error);
      toast.error("Failed to delete patient");
    }
  };

  const handleEditClinic = () => {
    const clinic = clinics.find(c => c.id === selectedClinic);
    if (clinic) {
      let openingHoursStr = "";
      if (typeof clinic.opening_hours === "string") {
        openingHoursStr = clinic.opening_hours;
      } else if (typeof clinic.opening_hours === "object" && clinic.opening_hours !== null) {
        openingHoursStr = JSON.stringify(clinic.opening_hours);
      }
      
      // Reconstruct GPS coordinates from lat/lng if available
      let gpsCoordinates = clinic.gps_coordinates || "";
      if (clinic.gps_lat && clinic.gps_lng) {
        gpsCoordinates = `${clinic.gps_lat},${clinic.gps_lng}`;
      }
      
      setEditForm({
        name: clinic.name || "",
        address: clinic.address || "",
        email: clinic.email || "",
        phones: Array.isArray(clinic.phones) ? clinic.phones.join(", ") : "",
        website: clinic.website || "",
        services: Array.isArray(clinic.services) ? clinic.services.join(", ") : "",
        gps_coordinates: gpsCoordinates,
        opening_hours: openingHoursStr,
        facebook_url: clinic.facebook_url || "",
        instagram_url: clinic.instagram_url || "",
        twitter_url: clinic.twitter_url || "",
        tiktok_url: clinic.tiktok_url || "",
        youtube_url: clinic.youtube_url || "",
        threads_url: clinic.threads_url || "",
        linkedin_url: clinic.linkedin_url || ""
      });
      setEditDialogOpen(true);
    }
  };

  const handleSaveClinic = async () => {
    try {
      const updateData = {
        name: editForm.name,
        address: editForm.address,
        email: editForm.email,
        phones: editForm.phones.split(",").map(p => p.trim()).filter(Boolean),
        website: editForm.website,
        services: editForm.services.split(",").map(s => s.trim()).filter(Boolean),
        gps_coordinates: editForm.gps_coordinates,
        opening_hours: editForm.opening_hours,
        facebook_url: editForm.facebook_url || null,
        instagram_url: editForm.instagram_url || null,
        twitter_url: editForm.twitter_url || null,
        tiktok_url: editForm.tiktok_url || null,
        youtube_url: editForm.youtube_url || null,
        threads_url: editForm.threads_url || null,
        linkedin_url: editForm.linkedin_url || null
      };

      await api.patch(`/clinics/${selectedClinic}`, updateData);
      toast.success("Clinic updated successfully");
      setEditDialogOpen(false);
      await loadClinics();
    } catch (error) {
      console.error("Failed to update clinic:", error);
      toast.error("Failed to update clinic");
    }
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setUserForm({
      full_name: "",
      email: "",
      password: "",
      role: "nurse",
      clinic_id: selectedClinic
    });
    setUserDialogOpen(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setUserForm({
      full_name: user.full_name || "",
      email: user.email,
      password: "",
      role: user.role,
      clinic_id: user.clinic_id || selectedClinic
    });
    setUserDialogOpen(true);
  };

  const handleSaveUser = async () => {
    try {
      if (selectedUser) {
        // Update user - allow changing name, email, role, password
        const updateData = {
          role: userForm.role,
          full_name: userForm.full_name,
          email: userForm.email
        };
        if (userForm.password) {
          updateData.password = userForm.password;
        }
        await api.patch(`/users/${selectedUser.id}`, updateData);
        toast.success("User updated successfully");
      } else {
        // Create new user
        await api.post("/users/", userForm);
        toast.success("User created successfully");
      }
      setUserDialogOpen(false);
      await loadUsers();
    } catch (error) {
      console.error("Failed to save user:", error);
      toast.error(error.response?.data?.detail || "Failed to save user");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    
    try {
      await api.delete(`/users/${userId}`);
      toast.success("User deleted successfully");
      await loadUsers();
    } catch (error) {
      console.error("Failed to delete user:", error);
      toast.error("Failed to delete user");
    }
  };

  const handleFileSelect = async (e) => {
    try {
      const file = e.target.files[0];
      if (!file) return;
      
      setCsvFile(file);
      
      // Parse CSV to get headers
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim()); // Remove empty lines
      
      if (lines.length === 0) {
        toast.error("CSV file is empty");
        return;
      }
      
      // Parse headers - handle quoted values
      const headerLine = lines[0];
      const headers = headerLine.split(',').map(h => h.trim().replace(/^["']|["']$/g, ''));
      
      setCsvHeaders(headers);
      setCsvData(text);
      
      // Auto-map columns based on common names
      const autoMapping = {};
      headers.forEach(header => {
        const lowerHeader = header.toLowerCase().trim();
        
        if (lowerHeader.includes('name') && !lowerHeader.includes('patient')) {
          autoMapping[header] = 'Full Name';
        } else if (lowerHeader.includes('dob') || lowerHeader.includes('birth') || lowerHeader.includes('date of birth')) {
          autoMapping[header] = 'Date of Birth';
        } else if (lowerHeader.includes('gender') || lowerHeader.includes('sex')) {
          autoMapping[header] = 'Gender';
        } else if (lowerHeader.includes('patient') && lowerHeader.includes('id')) {
          autoMapping[header] = 'Patient ID';
        } else if (lowerHeader.includes('email') || lowerHeader.includes('mail')) {
          autoMapping[header] = 'Email Address';
        } else if (lowerHeader.includes('phone') || lowerHeader.includes('mobile') || lowerHeader.includes('contact')) {
          autoMapping[header] = 'Phone';
        } else if (lowerHeader.includes('address') || lowerHeader.includes('location')) {
          autoMapping[header] = 'Address';
        } else if (lowerHeader.includes('history') || lowerHeader.includes('medical')) {
          autoMapping[header] = 'Medical History';
        }
      });
      
      setColumnMapping(autoMapping);
      setShowColumnMapper(true);
    } catch (error) {
      console.error("Error parsing CSV:", error);
      toast.error("Failed to parse CSV file. Please check the file format.");
      setCsvFile(null);
    }
  };

  const handleCsvImport = async () => {
    if (!csvData) {
      toast.error("Please select a CSV file");
      return;
    }

    // Validate required mappings
    const requiredFields = ['Full Name', 'Date of Birth', 'Gender'];
    const mappedFields = Object.values(columnMapping);
    const missingFields = requiredFields.filter(field => !mappedFields.includes(field));
    
    if (missingFields.length > 0) {
      toast.error(`Please map required fields: ${missingFields.join(', ')}`);
      return;
    }

    setImporting(true);
    try {
      // Create mapped CSV with correct column names
      const lines = csvData.split('\n');
      const originalHeaders = csvHeaders;
      
      // Create new header row with mapped names
      const newHeaders = originalHeaders.map(h => columnMapping[h] || h);
      const mappedCsv = [newHeaders.join(','), ...lines.slice(1)].join('\n');
      
      // Create new file with mapped data
      const blob = new Blob([mappedCsv], { type: 'text/csv' });
      const file = new File([blob], csvFile.name, { type: 'text/csv' });
      
      const formData = new FormData();
      formData.append('file', file);

      const result = await api.post('/patients/bulk-import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setImportResults(result);
      toast.success(result.message);
      await loadPatients();
      setShowColumnMapper(false);
    } catch (error) {
      console.error("Failed to import CSV:", error);
      toast.error(error.response?.data?.detail || "Failed to import CSV");
    } finally {
      setImporting(false);
    }
  };

  const handleCloseImportResults = () => {
    setImportResults(null);
    setCsvFile(null);
    setCsvImportOpen(false);
    setShowColumnMapper(false);
    setCsvHeaders([]);
    setCsvData([]);
    setColumnMapping({});
  };

  const downloadSampleCsv = () => {
    const sampleCsv = `Patient ID,Date of Birth,Full Name,Email Address,Phone,Gender,Address,Medical History
PAT001,1990-01-15,John Doe,john@example.com,9841234567,Male,"Ward 10, Ekantakuna, Lalitpur","Diabetics, High BP"
,1985-05-20,Jane Smith,jane@example.com,9851234567,Female,"Thamel, Kathmandu",Hypertension
PAT003,2000-12-10,Alice Johnson,,9861234567,Female,"Bhaktapur",No known conditions`;
    
    const blob = new Blob([sampleCsv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_patients_import.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };


  const calculateAge = (dob) => {
    if (!dob) return "N/A";
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const selectedClinicData = clinics.find(c => c.id === selectedClinic);

  if (!user || (user.role !== "superadmin" && user.role !== "clinic_admin")) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#fcfbf9" }}>
        <Card className="rounded-2xl border-[#e5e5e5] p-8">
          <p style={{ color: "#555555" }}>Access Denied: Admin privileges required.</p>
          <Button onClick={() => navigate("/dashboard")} className="mt-4 rounded-xl" style={{ backgroundColor: "#652D90" }}>
            Back to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        {/* Announcement Banner */}
        <AnnouncementBanner />
        
        <h1 className="text-4xl font-bold mb-8" style={{ color: "#652D90", fontFamily: "Poppins, sans-serif" }}>
          Admin Dashboard
        </h1>

        {/* Clinic Selector & Date Range */}
        <Card className="rounded-2xl border-[#e5e5e5] mb-6">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Clinic</Label>
                <Select value={selectedClinic} onValueChange={setSelectedClinic} disabled={isClinicAdmin}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Select clinic" />
                  </SelectTrigger>
                  <SelectContent>
                    {isSuperadmin && (
                      <SelectItem value="all">All Clinics</SelectItem>
                    )}
                    {clinics.map((clinic) => (
                      <SelectItem key={clinic.id} value={clinic.id}>
                        {clinic.name} {clinics.filter(c => c.name === clinic.name).length > 1 ? `(ID: ...${clinic.id.slice(-6)})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                  className="rounded-xl"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Clinic Info */}
        {selectedClinicData && (
          <Card className="rounded-2xl border-[#e5e5e5] mb-6">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span style={{ color: "#652D90" }}>{selectedClinicData.name}</span>
                <Button onClick={handleEditClinic} className="rounded-xl" style={{ backgroundColor: "#652D90" }}>
                  Edit Clinic
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm" style={{ color: "#555555" }}>
                <div>
                  <p><strong>Address:</strong> {selectedClinicData.address}</p>
                  <p><strong>Email:</strong> {selectedClinicData.email}</p>
                </div>
                <div>
                  <p><strong>Phone:</strong> {selectedClinicData.phones?.join(", ")}</p>
                  <p><strong>Website:</strong> {selectedClinicData.website || "N/A"}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Access Navigation */}
        {selectedClinic && (
          <Card className="rounded-2xl border-[#e5e5e5] mb-6">
            <CardHeader>
              <CardTitle style={{ color: "#652D90" }}>Quick Access</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Button
                  onClick={() => navigate('/audit-logs')}
                  variant="outline"
                  className="h-20 rounded-xl flex items-center justify-start gap-3 hover:bg-purple-50"
                  style={{ borderColor: "#652D90" }}
                >
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: "#f0e6f6" }}>
                    <Shield size={20} style={{ color: "#652D90" }} />
                  </div>
                  <div className="text-left">
                    <div className="font-semibold" style={{ color: "#652D90" }}>Audit Logs</div>
                    <div className="text-xs text-gray-600">View system activity</div>
                  </div>
                </Button>

                <Button
                  onClick={() => navigate('/user-management')}
                  variant="outline"
                  className="h-20 rounded-xl flex items-center justify-start gap-3 hover:bg-purple-50"
                  style={{ borderColor: "#652D90" }}
                >
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: "#f0e6f6" }}>
                    <UserCog size={20} style={{ color: "#652D90" }} />
                  </div>
                  <div className="text-left">
                    <div className="font-semibold" style={{ color: "#652D90" }}>User Management</div>
                    <div className="text-xs text-gray-600">Manage users</div>
                  </div>
                </Button>

                <Button
                  onClick={() => navigate('/patients')}
                  variant="outline"
                  className="h-20 rounded-xl flex items-center justify-start gap-3 hover:bg-purple-50"
                  style={{ borderColor: "#652D90" }}
                >
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: "#f0e6f6" }}>
                    <Users size={20} style={{ color: "#652D90" }} />
                  </div>
                  <div className="text-left">
                    <div className="font-semibold" style={{ color: "#652D90" }}>All Patients</div>
                    <div className="text-xs text-gray-600">View patient list</div>
                  </div>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Removed: Clinic SMS Footer - now in Clinic Settings */}

        {/* Tabs */}
        {selectedClinic ? (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-6">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="audit">Audit Logs</TabsTrigger>
              <TabsTrigger value="users">User Management</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview">
              {loading ? (
                <div className="text-center py-12">
                  <p style={{ color: "#555555" }}>Loading statistics...</p>
                </div>
              ) : stats ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                    <Card className="rounded-2xl border-[#e5e5e5]">
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium" style={{ color: "#555555" }}>Total Patients</p>
                            <p className="text-3xl font-bold mt-2" style={{ color: "#652D90" }}>{stats.total_patients}</p>
                          </div>
                          <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: "#f0e6f6" }}>
                            <Users size={24} style={{ color: "#652D90" }} />
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="rounded-2xl border-[#e5e5e5]">
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium" style={{ color: "#555555" }}>Lab Reports</p>
                            <p className="text-3xl font-bold mt-2" style={{ color: "#1fb25a" }}>{stats.lab_reports}</p>
                          </div>
                          <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: "#e6f7ee" }}>
                            <TrendingUp size={24} style={{ color: "#1fb25a" }} />
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="rounded-2xl border-[#e5e5e5]">
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium" style={{ color: "#555555" }}>Prescriptions</p>
                            <p className="text-3xl font-bold mt-2" style={{ color: "#ff9800" }}>{stats.prescriptions}</p>
                          </div>
                          <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: "#fff3e0" }}>
                            <FileText size={24} style={{ color: "#ff9800" }} />
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="rounded-2xl border-[#e5e5e5]">
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium" style={{ color: "#555555" }}>System Usage</p>
                            <p className="text-3xl font-bold mt-2" style={{ color: "#2196f3" }}>{stats.system_usage_logs}</p>
                          </div>
                          <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: "#e3f2fd" }}>
                            <Activity size={24} style={{ color: "#2196f3" }} />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <Card className="rounded-2xl border-[#e5e5e5]">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-2 text-sm" style={{ color: "#555555" }}>
                        <Calendar size={16} />
                        <span>Statistics for: {new Date(dateRange.start).toLocaleDateString('en-GB', {day: 'numeric', month: 'long', year: 'numeric'})} - {new Date(dateRange.end).toLocaleDateString('en-GB', {day: 'numeric', month: 'long', year: 'numeric'})}</span>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Upcoming Follow-ups */}
                  <Card className="rounded-2xl border-[#e5e5e5] mt-6">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle style={{ color: "#652D90" }}>Upcoming Follow-ups</CardTitle>
                        <Button 
                          onClick={() => setBulkNotificationOpen(true)}
                          className="rounded-xl" 
                          style={{ backgroundColor: "#652D90" }}
                        >
                          <Send size={16} className="mr-2" />
                          Bulk Notification
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {followUpsLoading ? (
                        <p style={{ color: "#555555" }}>Loading follow-ups...</p>
                      ) : followUps.length > 0 ? (
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Patient Name</TableHead>
                                <TableHead>Doctor Name</TableHead>
                                <TableHead>Follow-up Date</TableHead>
                                <TableHead>Email Status</TableHead>
                                <TableHead>SMS Status</TableHead>
                                <TableHead>Call Status</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {followUps.map((followUp) => (
                                <TableRow key={followUp.prescription_id}>
                                  <TableCell>
                                    <button
                                      onClick={() => navigate(`/patient/${followUp.patient_id}`)}
                                      className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-medium"
                                    >
                                      {followUp.patient_name}
                                    </button>
                                  </TableCell>
                                  <TableCell>{followUp.doctor_name || 'N/A'}</TableCell>
                                  <TableCell>
                                    {new Date(followUp.followup_datetime).toLocaleString('en-GB', {
                                      day: 'numeric',
                                      month: 'short',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </TableCell>
                                  <TableCell>
                                    {followUp.email_sent_at ? (
                                      <div className="text-xs">
                                        <span className="text-green-600">✓ Emailed</span>
                                        <br />
                                        <span className="text-gray-500">
                                          {new Date(followUp.email_sent_at).toLocaleDateString('en-GB')}
                                        </span>
                                      </div>
                                    ) : (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleSendIndividualEmail(followUp.prescription_id)}
                                        className="text-xs"
                                      >
                                        <Mail size={14} className="mr-1" />
                                        Send Email
                                      </Button>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {followUp.sms_sent_at ? (
                                      <div className="text-xs">
                                        <span className="text-green-600">✓ Sent</span>
                                        <br />
                                        <span className="text-gray-500">
                                          {new Date(followUp.sms_sent_at).toLocaleDateString('en-GB')}
                                        </span>
                                      </div>
                                    ) : (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleSendIndividualSms(followUp.prescription_id, followUp.patient_phone, followUp.patient_name)}
                                        className="text-xs"
                                      >
                                        <MessageCircle size={14} className="mr-1" />
                                        Send SMS
                                      </Button>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Select 
                                      value={followUp.call_status || "pending"}
                                      onValueChange={(value) => handleUpdateCallStatus(followUp.prescription_id, value)}
                                    >
                                      <SelectTrigger className="w-40 rounded-lg">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="pending">Pending</SelectItem>
                                        <SelectItem value="called">Called</SelectItem>
                                        <SelectItem value="not_picked">Not Picked</SelectItem>
                                        <SelectItem value="rescheduled">Rescheduled</SelectItem>
                                        <SelectItem value="confirmed">Confirmed</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      ) : (
                        <p style={{ color: "#555555" }}>No upcoming follow-ups in the next 30 days.</p>
                      )}
                    </CardContent>
                  </Card>
                </>
              ) : null}
            </TabsContent>

            {/* Audit Logs Tab */}
            <TabsContent value="audit">
              <Card className="rounded-2xl border-[#e5e5e5]">
                <CardHeader>
                  <CardTitle style={{ color: "#652D90" }}>Audit Logs</CardTitle>
                  <div className="flex gap-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="auditStartDate">Start Date</Label>
                      <Input
                        id="auditStartDate"
                        type="date"
                        value={auditDateFilter.startDate}
                        onChange={(e) => setAuditDateFilter({...auditDateFilter, startDate: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="auditEndDate">End Date</Label>
                      <Input
                        id="auditEndDate"
                        type="date"
                        value={auditDateFilter.endDate}
                        onChange={(e) => setAuditDateFilter({...auditDateFilter, endDate: e.target.value})}
                      />
                    </div>
                    <div className="flex items-end">
                      <Button onClick={loadAuditLogs} disabled={auditLoading}>
                        {auditLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                        Apply Filter
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {auditLoading ? (
                    <p style={{ color: "#555555" }}>Loading audit logs...</p>
                  ) : auditLogs.length > 0 ? (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Timestamp</TableHead>
                            <TableHead>User</TableHead>
                            <TableHead>Role</TableHead>
                            <TableHead>Action</TableHead>
                            <TableHead>Entity</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {auditLogs.map((log) => (
                            <TableRow key={log.id}>
                              <TableCell>{formatDateTime(log.timestamp)}</TableCell>
                              <TableCell>{log.user_email}</TableCell>
                              <TableCell>
                                <span className="px-2 py-1 rounded-full text-xs" style={{ 
                                  backgroundColor: log.role === 'superadmin' ? '#fee' : log.role === 'clinic_admin' ? '#efe' : '#eef',
                                  color: log.role === 'superadmin' ? '#c00' : log.role === 'clinic_admin' ? '#0a0' : '#00a'
                                }}>
                                  {log.role}
                                </span>
                              </TableCell>
                              <TableCell>{log.action}</TableCell>
                              <TableCell>{log.entity_type}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <p style={{ color: "#555555" }}>No audit logs found for this date range.</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* User Management Tab */}
            <TabsContent value="users">
              <Card className="rounded-2xl border-[#e5e5e5]">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span style={{ color: "#652D90" }}>User Management</span>
                    <Button onClick={handleCreateUser} className="rounded-xl" style={{ backgroundColor: "#652D90" }}>
                      <Plus size={16} className="mr-2" />
                      Create User
                    </Button>
                  </CardTitle>
                  <div className="mt-4 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <Input
                      type="text"
                      placeholder="Search by name, email, or role..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </CardHeader>
                <CardContent>
                  {usersLoading ? (
                    <p style={{ color: "#555555" }}>Loading users...</p>
                  ) : users.filter(u => {
                    if (!userSearch) return true;
                    const query = userSearch.toLowerCase();
                    return (
                      (u.full_name && u.full_name.toLowerCase().includes(query)) ||
                      (u.email && u.email.toLowerCase().includes(query)) ||
                      (u.role && u.role.toLowerCase().includes(query))
                    );
                  }).length > 0 ? (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Full Name</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Role</TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {users.filter(u => {
                            if (!userSearch) return true;
                            const query = userSearch.toLowerCase();
                            return (
                              (u.full_name && u.full_name.toLowerCase().includes(query)) ||
                              (u.email && u.email.toLowerCase().includes(query)) ||
                              (u.role && u.role.toLowerCase().includes(query))
                            );
                          }).map((u) => (
                            <TableRow key={u.id}>
                              <TableCell>{u.full_name || '-'}</TableCell>
                              <TableCell>{u.email}</TableCell>
                              <TableCell>
                                <span className="px-2 py-1 rounded-full text-xs" style={{ 
                                  backgroundColor: u.role === 'superadmin' ? '#fee' : u.role === 'clinic_admin' ? '#efe' : '#eef',
                                  color: u.role === 'superadmin' ? '#c00' : u.role === 'clinic_admin' ? '#0a0' : '#00a'
                                }}>
                                  {u.role}
                                </span>
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleEditUser(u)}
                                    className="rounded-lg"
                                  >
                                    <Edit size={14} className="mr-1" />
                                    Edit
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => handleDeleteUser(u.id)}
                                    className="rounded-lg"
                                  >
                                    <Trash2 size={14} className="mr-1" />
                                    Delete
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <p style={{ color: "#555555" }}>
                      {userSearch ? 'No users found matching your search.' : 'No users found for this clinic.'}
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Patients Tab */}
          </Tabs>
        ) : (
          <div className="text-center py-12">
            <p style={{ color: "#555555" }}>Select a clinic to view dashboard</p>
          </div>
        )}

      {/* Edit Clinic Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ color: "#652D90" }}>Edit Clinic</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Clinic Name *</Label>
              <Input id="name" value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} className="rounded-xl" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="address">Address *</Label>
              <Textarea id="address" value={editForm.address} onChange={(e) => setEditForm({...editForm, address: e.target.value})} className="rounded-xl" rows={2} />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">Email *</Label>
              <Input id="email" type="email" value={editForm.email} onChange={(e) => setEditForm({...editForm, email: e.target.value})} className="rounded-xl" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="phones">Phone Numbers (comma-separated) *</Label>
              <Input id="phones" value={editForm.phones} onChange={(e) => setEditForm({...editForm, phones: e.target.value})} placeholder="+977-1-4123456, +977-9876543210" className="rounded-xl" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="website">Website</Label>
              <Input id="website" value={editForm.website} onChange={(e) => setEditForm({...editForm, website: e.target.value})} placeholder="https://example.com" className="rounded-xl" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="services">Services (comma-separated)</Label>
              <Textarea id="services" value={editForm.services} onChange={(e) => setEditForm({...editForm, services: e.target.value})} placeholder="Lab Tests, X-Ray, ECG" className="rounded-xl" rows={2} />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="gps">GPS Coordinates</Label>
              <Input id="gps" value={editForm.gps_coordinates} onChange={(e) => setEditForm({...editForm, gps_coordinates: e.target.value})} placeholder="27.7172, 85.3240" className="rounded-xl" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="hours">Opening Hours</Label>
              <Input id="hours" value={editForm.opening_hours} onChange={(e) => setEditForm({...editForm, opening_hours: e.target.value})} placeholder="Mon-Fri: 9AM-5PM, Sat: 9AM-1PM" className="rounded-xl" />
            </div>
            
            {/* Social Media Section */}
            <div className="grid gap-4 pt-4 border-t">
              <h4 className="font-semibold text-gray-700">Social Media Links</h4>
              <div className="grid gap-2">
                <Label htmlFor="facebook">Facebook URL</Label>
                <Input id="facebook" value={editForm.facebook_url || ''} onChange={(e) => setEditForm({...editForm, facebook_url: e.target.value})} placeholder="https://facebook.com/yourclinic" className="rounded-xl" />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="instagram">Instagram URL</Label>
                <Input id="instagram" value={editForm.instagram_url || ''} onChange={(e) => setEditForm({...editForm, instagram_url: e.target.value})} placeholder="https://instagram.com/yourclinic" className="rounded-xl" />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="twitter">Twitter URL</Label>
                <Input id="twitter" value={editForm.twitter_url || ''} onChange={(e) => setEditForm({...editForm, twitter_url: e.target.value})} placeholder="https://twitter.com/yourclinic" className="rounded-xl" />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="tiktok">TikTok URL</Label>
                <Input id="tiktok" value={editForm.tiktok_url || ''} onChange={(e) => setEditForm({...editForm, tiktok_url: e.target.value})} placeholder="https://tiktok.com/@yourclinic" className="rounded-xl" />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="youtube">YouTube URL</Label>
                <Input id="youtube" value={editForm.youtube_url || ''} onChange={(e) => setEditForm({...editForm, youtube_url: e.target.value})} placeholder="https://youtube.com/@yourclinic" className="rounded-xl" />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="threads">Threads URL</Label>
                <Input id="threads" value={editForm.threads_url || ''} onChange={(e) => setEditForm({...editForm, threads_url: e.target.value})} placeholder="https://threads.net/@yourclinic" className="rounded-xl" />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="linkedin">LinkedIn URL</Label>
                <Input id="linkedin" value={editForm.linkedin_url || ''} onChange={(e) => setEditForm({...editForm, linkedin_url: e.target.value})} placeholder="https://linkedin.com/company/yourclinic" className="rounded-xl" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={handleSaveClinic} className="rounded-xl" style={{ backgroundColor: "#652D90" }} disabled={!editForm.name || !editForm.address || !editForm.email || !editForm.phones}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Management Dialog */}
      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ color: "#652D90" }}>{selectedUser ? "Edit User" : "Create User"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="user-name">Full Name</Label>
              <Input
                id="user-name"
                type="text"
                value={userForm.full_name}
                onChange={(e) => setUserForm({...userForm, full_name: e.target.value})}
                className="rounded-xl"
                placeholder="John Doe"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="user-email">Email *</Label>
              <Input
                id="user-email"
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                className="rounded-xl"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="user-password">Password {selectedUser ? "(leave blank to keep current)" : "*"}</Label>
              <Input
                id="user-password"
                type="password"
                value={userForm.password}
                onChange={(e) => setUserForm({...userForm, password: e.target.value})}
                className="rounded-xl"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="user-role">Role *</Label>
              <Select value={userForm.role} onValueChange={(val) => setUserForm({...userForm, role: val})}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nurse">Nurse</SelectItem>
                  <SelectItem value="clinic_admin">Clinic Admin</SelectItem>
                  {isSuperadmin && <SelectItem value="superadmin">Superadmin</SelectItem>}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserDialogOpen(false)} className="rounded-xl">Cancel</Button>
            <Button
              onClick={handleSaveUser}
              className="rounded-xl"
              style={{ backgroundColor: "#652D90" }}
              disabled={!userForm.email || (!selectedUser && !userForm.password)}
            >
              {selectedUser ? "Update User" : "Create User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Notification Dialog */}
      <Dialog open={bulkNotificationOpen} onOpenChange={setBulkNotificationOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ color: "#652D90" }}>Send Bulk Follow-up Notifications</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <p className="text-sm" style={{ color: "#555555" }}>
              Select a date range to send follow-up email reminders to all patients with appointments in that period.
            </p>
            <div className="grid gap-2">
              <Label htmlFor="bulk-start">Start Date</Label>
              <Input
                id="bulk-start"
                type="date"
                value={bulkDateRange.startDate}
                onChange={(e) => setBulkDateRange({...bulkDateRange, startDate: e.target.value})}
                className="rounded-xl"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="bulk-end">End Date</Label>
              <Input
                id="bulk-end"
                type="date"
                value={bulkDateRange.endDate}
                onChange={(e) => setBulkDateRange({...bulkDateRange, endDate: e.target.value})}
                className="rounded-xl"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setBulkNotificationOpen(false)} 
              className="rounded-xl"
              disabled={sendingBulk}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkNotification}
              className="rounded-xl"
              style={{ backgroundColor: "#652D90" }}
              disabled={sendingBulk || !bulkDateRange.startDate || !bulkDateRange.endDate}
            >
              {sendingBulk ? "Sending..." : "Send Notifications"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog open={csvImportOpen} onOpenChange={(open) => {
        setCsvImportOpen(open);
        if (!open) {
          setCsvFile(null);
          setShowColumnMapper(false);
          setCsvHeaders([]);
          setCsvData([]);
          setColumnMapping({});
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ color: "#652D90" }}>
              {showColumnMapper ? 'Map CSV Columns' : 'Import Patients from CSV'}
            </DialogTitle>
          </DialogHeader>
          
          {!showColumnMapper ? (
            <div className="space-y-4">
              <div>
                <p className="text-sm mb-4" style={{ color: "#555555" }}>
                  Upload a CSV file with patient information. You'll be able to map your columns to the correct fields in the next step.
                </p>
                <p className="text-sm mb-2" style={{ color: "#555555" }}>
                  <strong>Required fields:</strong> Full Name, Date of Birth, Gender
                </p>
                <p className="text-sm mb-4" style={{ color: "#555555" }}>
                  <strong>Optional fields:</strong> Patient ID, Email Address, Phone, Address, Medical History
                </p>
              </div>
              
              <div className="border-2 border-dashed rounded-xl p-6 text-center">
                <Input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="rounded-xl"
                />
                {csvFile && (
                  <p className="mt-2 text-sm text-green-600">
                    Selected: {csvFile.name}
                  </p>
                )}
              </div>

              <Button
                variant="outline"
                onClick={downloadSampleCsv}
                className="rounded-xl w-full"
              >
                Download Sample CSV Template
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-xl">
                <p className="text-sm text-blue-800 mb-2">
                  <strong>Column Mapping</strong>
                </p>
                <p className="text-xs text-blue-600">
                  Map your CSV columns to the system fields. We've auto-detected some mappings based on common column names. Required fields are marked with *.
                </p>
              </div>
              
              <div className="border rounded-xl overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-1/3">Your CSV Column</TableHead>
                      <TableHead className="w-1/3">Maps To</TableHead>
                      <TableHead className="w-1/3">Sample Data</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {csvHeaders.map((header, idx) => {
                      // Safely extract sample data - handle CSV with quoted fields
                      let sampleRow = '';
                      try {
                        const dataLines = csvData.split('\n').filter(line => line.trim());
                        if (dataLines.length > 1) {
                          const firstDataLine = dataLines[1];
                          // Parse CSV line properly handling quoted fields
                          const values = [];
                          let currentValue = '';
                          let insideQuotes = false;
                          
                          for (let i = 0; i < firstDataLine.length; i++) {
                            const char = firstDataLine[i];
                            
                            if (char === '"') {
                              insideQuotes = !insideQuotes;
                            } else if (char === ',' && !insideQuotes) {
                              values.push(currentValue.trim());
                              currentValue = '';
                            } else {
                              currentValue += char;
                            }
                          }
                          values.push(currentValue.trim()); // Push last value
                          
                          sampleRow = values[idx] || '';
                        }
                      } catch (e) {
                        sampleRow = '';
                      }
                      
                      return (
                        <TableRow key={idx}>
                          <TableCell className="font-semibold">{header}</TableCell>
                          <TableCell>
                            <Select 
                              value={columnMapping[header] || "skip"}
                              onValueChange={(value) => {
                                const newMapping = {...columnMapping};
                                if (value === "skip") {
                                  delete newMapping[header];
                                } else {
                                  newMapping[header] = value;
                                }
                                setColumnMapping(newMapping);
                              }}
                            >
                              <SelectTrigger className="rounded-lg">
                                <SelectValue placeholder="Select field..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="skip">-- Skip this column --</SelectItem>
                                <SelectItem value="Patient ID">Patient ID</SelectItem>
                                <SelectItem value="Full Name">Full Name *</SelectItem>
                                <SelectItem value="Date of Birth">Date of Birth *</SelectItem>
                                <SelectItem value="Gender">Gender *</SelectItem>
                                <SelectItem value="Email Address">Email Address</SelectItem>
                                <SelectItem value="Phone">Phone</SelectItem>
                                <SelectItem value="Address">Address</SelectItem>
                                <SelectItem value="Medical History">Medical History</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-xs text-gray-500 max-w-[250px]" title={sampleRow}>
                            <div className="truncate">{sampleRow || 'No data'}</div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              <div className="bg-yellow-50 p-3 rounded-xl">
                <p className="text-xs text-yellow-800">
                  <strong>Note:</strong> Required fields must be mapped: Full Name, Date of Birth, Gender
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                if (showColumnMapper) {
                  setShowColumnMapper(false);
                  setCsvFile(null);
                  setCsvHeaders([]);
                  setCsvData([]);
                  setColumnMapping({});
                } else {
                  setCsvImportOpen(false);
                  setCsvFile(null);
                }
              }}
              className="rounded-xl"
              disabled={importing}
            >
              {showColumnMapper ? 'Back' : 'Cancel'}
            </Button>
            {!showColumnMapper ? (
              <Button
                onClick={() => {
                  if (!csvFile) {
                    toast.error("Please select a CSV file");
                    return;
                  }
                  // File is already parsed in handleFileSelect
                }}
                className="rounded-xl"
                style={{ backgroundColor: "#652D90" }}
                disabled={!csvFile}
              >
                Next: Map Columns
              </Button>
            ) : (
              <Button
                onClick={handleCsvImport}
                className="rounded-xl"
                style={{ backgroundColor: "#652D90" }}
                disabled={importing}
              >
                {importing ? "Importing..." : "Import Patients"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Results Dialog */}
      <Dialog open={!!importResults} onOpenChange={() => setImportResults(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ color: "#652D90" }}>Import Results</DialogTitle>
          </DialogHeader>
          {importResults && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-2xl font-bold text-blue-600">{importResults.total_rows}</p>
                    <p className="text-sm text-gray-600">Total Rows</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-2xl font-bold text-green-600">{importResults.added_count}</p>
                    <p className="text-sm text-gray-600">Successfully Added</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-2xl font-bold text-red-600">{importResults.discarded_count}</p>
                    <p className="text-sm text-gray-600">Discarded</p>
                  </CardContent>
                </Card>
              </div>

              {importResults.added_count > 0 && (
                <div>
                  <h3 className="font-semibold mb-2 text-green-600">✓ Successfully Added Patients ({importResults.added_count})</h3>
                  <div className="border rounded-xl overflow-hidden max-h-48 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Row</TableHead>
                          <TableHead>Patient ID</TableHead>
                          <TableHead>Full Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Phone</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {importResults.added_patients.map((p, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{p.row}</TableCell>
                            <TableCell className="font-mono text-xs">{p.patient_id}</TableCell>
                            <TableCell>{p.full_name}</TableCell>
                            <TableCell>{p.email || '—'}</TableCell>
                            <TableCell>{p.phone || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {importResults.discarded_count > 0 && (
                <div>
                  <h3 className="font-semibold mb-2 text-red-600">✗ Discarded Patients ({importResults.discarded_count})</h3>
                  <div className="border rounded-xl overflow-hidden max-h-48 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Row</TableHead>
                          <TableHead>Patient ID</TableHead>
                          <TableHead>Full Name</TableHead>
                          <TableHead>Reason</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {importResults.discarded_patients.map((p, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{p.row}</TableCell>
                            <TableCell className="font-mono text-xs">{p.patient_id || '—'}</TableCell>
                            <TableCell>{p.full_name || '—'}</TableCell>
                            <TableCell className="text-red-600 text-sm">{p.reason}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={handleCloseImportResults} className="rounded-xl" style={{ backgroundColor: "#652D90" }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Individual SMS Preview Dialog */}
      <Dialog open={smsPreviewOpen} onOpenChange={setSmsPreviewOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm SMS Send</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Recipient</Label>
              <p className="text-lg font-medium">{smsToSend?.patientName}</p>
              <p className="text-sm text-gray-600">{smsToSend?.patientPhone}</p>
            </div>
            <div>
              <Label>Message</Label>
              <div className="p-4 bg-gray-50 rounded-lg whitespace-pre-wrap text-sm">
                {smsToSend?.message}
              </div>
            </div>
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                ⚠️ <strong>Cost:</strong> {Math.ceil((smsToSend?.message || "").length / 160)} SMS credit(s) will be deducted
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {setSmsPreviewOpen(false); setSmsToSend(null);}}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmSendSms}
              style={{ backgroundColor: "#652D90" }}
            >
              <Send size={16} className="mr-2" />
              Confirm & Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </Layout>
  );
};
